import React, { useState } from 'react';
import axios from 'axios';
import './Login.css';

function Login({ onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!email || !password) {
      setErrorMessage('Please enter both email and password');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post('/api/auth/login', {
        email: email.trim(),
        password: password,
      });

      if (response.data && response.data.token) {
        onLoginSuccess(response.data);
      } else {
        setErrorMessage('Authentication response was invalid.');
      }
    } catch (err) {
      console.error('Login error:', err);
      const serverMsg =
        err.response?.data?.error ||
        (err.response?.status === 401
          ? 'Invalid email or password. SuperAdmin access only.'
          : 'Unable to connect to server. Please ensure backend is running.');
      setErrorMessage(serverMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page-wrapper">
      <div className="login-card-container">
        {/* Brand Header */}
        <div className="login-header">
          <div className="login-logos-container">
            <div className="login-logo-pill">
              <img
                src="/logos/PMC_logo.png"
                alt="PMC Logo"
                className="login-brand-img logo-pmc"
                onError={(e) => {
                  e.target.src = '/api/visitors/logo/PMC_logo.png';
                }}
              />
            </div>
            <div className="login-logo-divider">•</div>
            <div className="login-logo-pill">
              <img
                src="/logos/Innover_logo.png"
                alt="Innover Logo"
                className="login-brand-img logo-innover"
                onError={(e) => {
                  e.target.src = '/api/visitors/logo/Innover_logo.png';
                }}
              />
            </div>
          </div>
          <h1 className="login-title">Visitor Management System</h1>
          <div className="login-badge">
            <span className="badge-shield">🛡️</span> SUPERADMIN PORTAL
          </div>
          <p className="login-subtitle">
            Sign in with authorized superadmin credentials to manage visitors, passes, and records.
          </p>
        </div>

        {/* Error Notification */}
        {errorMessage && (
          <div className="login-error-alert" role="alert">
            <span className="error-icon">⚠️</span>
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Login Form */}
        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-form-group">
            <label htmlFor="login-email">SuperAdmin Email</label>
            <div className="input-with-icon">
              <span className="field-icon">✉️</span>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your admin email"
                required
                autoComplete="username"
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="login-form-group">
            <label htmlFor="login-password">Password</label>
            <div className="input-with-icon">
              <span className="field-icon">🔒</span>
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
                disabled={isLoading}
              />
              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? 'Hide password' : 'Show password'}
                tabIndex="-1"
              >
                {showPassword ? '👁️' : '🙈'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn-login-submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="loading-spinner-wrapper">
                <span className="spinner"></span> Authenticating...
              </span>
            ) : (
              '🔐 Sign In to Dashboard'
            )}
          </button>
        </form>

        <div className="login-footer-notice">
          🔒 Secure Access Protected • Authorized Personnel Only
        </div>
      </div>
    </div>
  );
}

export default Login;
